
import { DepthFormat, invertDepthCompare } from '../CommonRenderTargets';
import { getBufferLayout, getPipelineHash } from '../Pipelines';
import { StateTrackingEncoder } from '../StateTrackingEncoder';
import { getEdgeShader } from './EdgeShader';

/** @import { MultiDrawBatch } from '../gpudrivenrender/MultiDrawBatch' */
/** @import { VertexBuffer } from '../VertexBuffer' */

export class EdgePipeline {

  #renderer;
  /** @type {GPUDevice} */
  #device;

  #pipelines = new Map();
  /** Ghosted (dimmed) pass pipelines; cleared when #ditheredGhosting changes so they are recreated with the new constant. */
  #ghostedPipelines = new Map();
  /** Dithering for dimmed (ghosted) pass. When changed, only #ghostedPipelines are reset. */
  #ditheredGhosting = false;
  /** @type {GPUBindGroupLayout[]} */
  #activeBindGroupLayout = [];
  /** @type {GPUColorTargetState[]} */
  #activeTargetsList = [];
  /** @type {VertexBuffer} */
  #vb;

  constructor(renderer) {
    this.#renderer = renderer;
    this.#device = renderer.getDevice();
  }

  #createPipeline(key, geometry, material, dithered, hasInstanceIdMap, useWboit) {
    const isUnorm16 = geometry.hasUnorm16Layout?.();
    const shader = this.#device.createShaderModule({
      label: 'edge-shader',
      code: getEdgeShader(hasInstanceIdMap, useWboit, isUnorm16)
    });

    const depthCompare = material.depthTest ? invertDepthCompare(material.depthFunc || 'less-equal') : 'always';
    const sampleCount = this.#renderer.getMSAASampleCount();

    const pipeline = this.#device.createRenderPipeline({
      label: 'edge-pipeline',
      layout: this.#device.createPipelineLayout({
        bindGroupLayouts: this.#activeBindGroupLayout
      }),
      vertex: {
        module: shader,
        entryPoint: 'vsmain',
        buffers: getBufferLayout(geometry, false, false, false, false)
      },
      fragment: {
        module: shader,
        entryPoint: 'psmain',
        constants: { dithered: dithered ? 1 : 0 },
        targets: this.#activeTargetsList
      },
      primitive: { topology: 'line-list', cullMode: 'none' },
      depthStencil: {
        depthWriteEnabled: useWboit ? false : material.depthWrite,
        depthCompare,
        format: DepthFormat,
        // "depthBias, depthBiasSlopeScale, and depthBiasClamp have no effect on 'point-list', 'line-list', and
        // 'line-strip' primitives, and must be 0." ...from https://www.w3.org/TR/webgpu/#depth-stencil-state
        depthBias: 0,
        depthBiasSlopeScale: 0
      },
      multisample: {
        count: sampleCount
      }
    });

    return pipeline;
  }

  /**
   * Set dithering for the edges rendered in ghosted-mode. All pipelines for ghosted edges are invalidated on value change.
   * @param {boolean} [dithered=false] - Defaults to false (no dithering).
   * @returns {boolean} true if the value changed, intended for caller to invalidate render bundles.
   */
  setDitheredGhosting(dithered = false) {
    if (this.#ditheredGhosting === dithered) return false;

    this.#ditheredGhosting = dithered;
    this.#ghostedPipelines.clear();
    return true;
  }

  /**
   * @param {GPUBindGroupLayout[]} layouts
   * @param {GPUColorTargetState[]} targets
   */
  reset(layouts, targets) {
    this.#activeBindGroupLayout = layouts;
    this.#activeTargetsList = targets;
    this.#vb = this.#renderer.getVB();
  }

  /**
   * @param {boolean} ghosted - if true, use ghosted key and #ghostedPipelines (dithered = #ditheredGhosting)
   * @returns {GPURenderPipeline}
   */
  #getOrCreatePipeline(geometry, material, hasInstanceIdMap, useWboit, ghosted) {
    const hasTextures = ghosted; // abuse this flag to distinguish keys from non-ghosted edge pipelines (@todo: generalize flag)
    const hash = getPipelineHash(geometry, material, false, false, hasTextures, false, hasInstanceIdMap, useWboit);
    const pipelines = ghosted ? this.#ghostedPipelines : this.#pipelines;

    let pipeline = pipelines.get(hash);
    if (pipeline) return pipeline;

    const dithered = ghosted ? this.#ditheredGhosting : false;
    pipeline = this.#createPipeline(hash, geometry, material, dithered, hasInstanceIdMap, useWboit);
    pipelines.set(hash, pipeline);

    return pipeline;
  }

  #preparePipeline(passEncoder, geometry, material, hasInstanceIdMap, useWboit) {
    const pipeline = this.#getOrCreatePipeline(geometry, material, hasInstanceIdMap, useWboit, false);
    passEncoder.setPipelineIfNeeded(pipeline);
  }

  drawOne(passEncoder, objectIndex, geometry, material, useWboit) {
    // Point geometry has no edges and don't draw line geometry as its own edge -- it's already drawn in the main color pass
    if (geometry.isLines || geometry.isPoints) {
      return;
    }

    this.#preparePipeline(passEncoder, geometry, material, false, useWboit);
    this.#vb.drawEdges(passEncoder, geometry, objectIndex);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {MultiDrawCall} multiDrawCall
   * @param {MultiDrawBatch} mdBatch
   */
  drawMulti(passEncoder, multiDrawCall, mdBatch) {
    const geometry = multiDrawCall.firstGeometry;
    const material = multiDrawCall.firstMaterial;
    const hasInstanceIdMap = mdBatch.enableCulling;
    const materialTextureMask = this.#preparePipeline(
      passEncoder, geometry, material, hasInstanceIdMap, false);

    this.#vb.drawMulti(passEncoder, multiDrawCall, mdBatch, true);

    return materialTextureMask;
  }

  /**
   * Dimmed (ghosted) pass: uses #ghostedPipelines and current #ditheredGhosting for pipeline constant.
   */
  drawOneGhosted(passEncoder, objectIndex, geometry, material) {
    // Point geometry has no edges
    if (geometry.isPoints) {
      return;
    }

    const pipeline = this.#getOrCreatePipeline(geometry, material, false, false, true);
    passEncoder.setPipelineIfNeeded(pipeline);

    if (geometry.isLines) {
      this.#vb.draw(passEncoder, geometry, objectIndex);
    } else {
      this.#vb.drawEdges(passEncoder, geometry, objectIndex);
    }
  }

  resetPipelines() {
    this.#pipelines.clear();
    this.#ghostedPipelines.clear();
  }

}